import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Button } from 'antd';
import {
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  UserAddOutlined,
  EditOutlined,
  AuditOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getGradeStatistics } from '../../services/grade';
import { getStudentStatistics } from '../../services/student';
import '../Admin/Dashboard.css';

const CampusDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState({
    studentCount: 0,
    totalGrades: 0,
    pendingGrades: 0,
    approvedGrades: 0,
  });
  const [campusInfo, setCampusInfo] = useState<any>(null);

  // 获取用户角色
  const getUserRole = (): string => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      return JSON.parse(storedUserInfo).role || '';
    }
    return '';
  };

  const userRole = getUserRole();
  const isUploader = userRole === 'uploader';

  useEffect(() => {
    const storedCampusInfo = localStorage.getItem('campusInfo');
    if (storedCampusInfo) {
      setCampusInfo(JSON.parse(storedCampusInfo));
    }
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const [gradeRes, studentRes] = await Promise.all([
        getGradeStatistics(),
        getStudentStatistics(),
      ]);
      
      const statusStats = gradeRes.statusStats || [];
      const pendingCount = statusStats.find((s: any) => s.status === 'pending')?.count || 0;
      const approvedCount = statusStats.find((s: any) => s.status === 'approved')?.count || 0;
      
      setStatistics({
        studentCount: studentRes.total || 0,
        totalGrades: pendingCount + approvedCount,
        pendingGrades: pendingCount,
        approvedGrades: approvedCount,
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: '学生总数',
      value: statistics.studentCount,
      icon: <TeamOutlined />,
      color: 'primary',
      path: '/campus/students',
      trend: '+15%',
    },
    {
      title: '成绩记录',
      value: statistics.totalGrades,
      icon: <FileTextOutlined />,
      color: 'success',
      path: '/campus/grades',
      trend: '+8%',
    },
    {
      title: '待审核',
      value: statistics.pendingGrades,
      icon: <ClockCircleOutlined />,
      color: 'warning',
      path: '/campus/grade-review',
      trend: '-3%',
    },
    {
      title: '已审核',
      value: statistics.approvedGrades,
      icon: <CheckCircleOutlined />,
      color: 'info',
      path: '/campus/grades',
      trend: '+12%',
    },
  ];

  // 快捷操作 - 根据角色动态显示
  const getQuickActions = () => {
    const baseActions = [
      { title: '添加学生', icon: <UserAddOutlined />, path: '/campus/students', color: '#4F46E5' },
      { title: '录入成绩', icon: <EditOutlined />, path: '/campus/grades', color: '#10B981' },
    ];

    // 只有校区负责人才能看到审核和统计功能
    if (!isUploader) {
      baseActions.push(
        { title: '成绩审核', icon: <AuditOutlined />, path: '/campus/grade-review', color: '#EA580C' },
        { title: '成绩统计', icon: <BarChartOutlined />, path: '/campus/statistics', color: '#0891B2' }
      );
    }

    return baseActions;
  };

  const quickActions = getQuickActions();

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* 页面标题 */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">
            {campusInfo?.campusName || '校区控制台'}
          </h1>
          <p className="dashboard-subtitle">欢迎回来，管理您的校区数据</p>
        </div>
        <Button 
          type="primary" 
          icon={<UserAddOutlined />}
          className="dashboard-notify-btn"
          onClick={() => navigate('/campus/students')}
        >
          添加学生
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]} className="dashboard-stats-row">
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card 
              className={`stat-card stat-card-${card.color}`}
              onClick={() => navigate(card.path)}
              hoverable
            >
              <div className="stat-card-content">
                <div className={`stat-card-icon stat-card-icon-${card.color}`}>
                  {card.icon}
                </div>
                <div className="stat-card-info">
                  <div className="stat-card-title">{card.title}</div>
                  <div className="stat-card-value">{card.value}</div>
                  <div className="stat-card-trend">
                    <ArrowUpOutlined /> {card.trend}
                    <span className="stat-card-trend-label">较上月</span>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 快捷操作 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card className="dashboard-card" title="快捷操作">
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <div 
                  key={index} 
                  className="quick-action-item"
                  onClick={() => navigate(action.path)}
                >
                  <div 
                    className="quick-action-icon"
                    style={{ backgroundColor: `${action.color}15`, color: action.color }}
                  >
                    {action.icon}
                  </div>
                  <span className="quick-action-title">{action.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="dashboard-card" title="使用指南">
            <div className="announcement-list">
              <div className="announcement-item">
                <div className="announcement-dot" style={{ background: '#4F46E5' }} />
                <div className="announcement-content">
                  <div className="announcement-title">添加学生信息</div>
                  <div className="announcement-time">在"学生管理"中添加</div>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-dot" style={{ background: '#10B981' }} />
                <div className="announcement-content">
                  <div className="announcement-title">录入各科成绩</div>
                  <div className="announcement-time">在"成绩管理"中录入</div>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-dot" style={{ background: '#EA580C' }} />
                <div className="announcement-content">
                  <div className="announcement-title">审核成绩记录</div>
                  <div className="announcement-time">在"成绩审核"中处理</div>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CampusDashboard;
